import { TraceBehavior } from "./tracebehavior";
import { SphereColliderData } from "../scenedata/behaviors/spherecolliderdata";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";

import { COLLIDER_MESH_SUFFIX } from "./boxcollider";
import { Logger } from "../utils/Logger";

/**
 * Sphere collider behavior for physics and interaction detection.
 * Defines a sphere-shaped collision volume.
 *
 * Creates an invisible picker mesh for selection in play mode.
 * The picker mesh matches the collider's radius and center offset.
 *
 * ## Usage
 *
 * ```typescript
 * const collider = sceneObject.getBehavior(SphereCollider);
 * const data = collider.getData();
 * data.radius = 1.5;
 * data.center = { x: 0, y: 0.5, z: 0 };
 * await collider.commit(data);
 * ```
 */
export class SphereCollider extends TraceBehavior<SphereColliderData> {
    static readonly behaviorType = "SphereCollider" as const;

    /** Invisible mesh used for picking in play mode */
    private _pickerMesh: Mesh | null = null;

    /**
     * Get the picker mesh (for external access, e.g., mode switching).
     */
    public getPickerMesh(): Mesh | null {
        return this._pickerMesh;
    }

    protected onAttach(target: TransformNode): void {
        this._createPickerMesh(target);
    }

    protected onDetach(): void {
        this._disposePickerMesh();
    }

    protected async applyCommit(_patch: Partial<SphereColliderData>): Promise<void> {
        if (this.attachedNode) {
            // Recompute native policy for the rebuilt mesh, including hover
            // and Renderer.pickThrough, without a play-mode toggle.
            this._disposePickerMesh();
            this._createPickerMesh(this.attachedNode);
            this.sceneObject.traceScene.refreshMeshPickability?.(this.sceneObject);
        }
    }

    /**
     * Toggle debug visualization of the collider mesh.
     * When visible, shows a semi-transparent blue wireframe sphere.
     */
    public setDebugVisible(visible: boolean): void {
        if (!this._pickerMesh) return;

        // Use visibility (0-1) instead of isVisible to keep picking working
        this._pickerMesh.visibility = visible ? 0.3 : 0;

        if (visible && !this._pickerMesh.material) {
            const mat = new StandardMaterial(
                `${this.sceneObject.name}_collider_debug_mat`,
                this.sceneObject.babylonScene
            );
            mat.diffuseColor = new Color3(0, 0.5, 1); // Blue
            mat.wireframe = true;
            this._pickerMesh.material = mat;
        }
    }

    private _createPickerMesh(parent: TransformNode): void {
        const data = this._resolved;
        const radius = data.radius ?? 0.5;
        const center = data.center ?? [0, 0, 0];

        Logger.debug(`[SphereCollider] Creating picker mesh for "${this.sceneObject.name}": radius=${radius}, center=(${center[0]}, ${center[1]}, ${center[2]})`);

        // Create invisible sphere mesh for picking
        // Use 16 segments for reasonable picking accuracy without too many triangles
        this._pickerMesh = MeshBuilder.CreateSphere(
            `${this.sceneObject.name}${COLLIDER_MESH_SUFFIX}`,
            { diameter: radius * 2, segments: 16 },
            this.sceneObject.babylonScene
        );

        // Position at center offset
        this._pickerMesh.position.set(center[0], center[1], center[2]);

        // Parent to the transform node
        this._pickerMesh.parent = parent;

        // Make transparent but pickable
        // Note: isVisible=false causes Babylon to skip picking, so we use visibility=0 instead
        this._pickerMesh.visibility = 0;
        this._pickerMesh.isPickable = true;

        Logger.debug(`[SphereCollider] Picker mesh created: "${this._pickerMesh.name}", isPickable=${this._pickerMesh.isPickable}`);
    }

    private _disposePickerMesh(): void {
        if (this._pickerMesh) {
            this._pickerMesh.dispose();
            this._pickerMesh = null;
        }
    }
}
